import {z} from 'zod';

import {omneticApi} from '@dms/api/base';

const RECORD_HEADER_COLLAPSED_SETTING_ID = 'record-header-collapsed';

// The backend fallback is false. A value of any other shape also reads as an expanded header.
const RecordHeaderCollapsedSchema = z.boolean().catch(false);

type SetRecordHeaderCollapsedApiArg = {
  userId: string;
  isCollapsed: boolean;
};

// One user setting for every record detail with a collapsible header. The setter updates the cached
// value at once and rolls it back when the request fails, so the header does not wait for the server.
export const recordHeaderCollapsedApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getRecordHeaderCollapsed: build.query<boolean, void>({
      query: () => ({
        url: `/dms/v1/settings/${RECORD_HEADER_COLLAPSED_SETTING_ID}/effective-value/myself/without-reference`,
      }),
      transformResponse: (response: unknown) => RecordHeaderCollapsedSchema.parse(response),
    }),
    setRecordHeaderCollapsed: build.mutation<void, SetRecordHeaderCollapsedApiArg>({
      query: (arg) => ({
        url: `/dms/v1/settings/${RECORD_HEADER_COLLAPSED_SETTING_ID}/user/${arg.userId}`,
        method: 'POST',
        body: {value: arg.isCollapsed, force: false},
      }),
      // The undo restores the value cached when this save started. That is the saved value only when
      // saves do not overlap, so a caller starts a new save only after the previous one settles.
      onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
        const patchResult = dispatch(
          recordHeaderCollapsedApi.util.updateQueryData(
            'getRecordHeaderCollapsed',
            undefined,
            () => arg.isCollapsed
          )
        );
        await queryFulfilled.catch(patchResult.undo);
      },
    }),
  }),
});

export const {useGetRecordHeaderCollapsedQuery, useSetRecordHeaderCollapsedMutation} =
  recordHeaderCollapsedApi;
